import { useCallback, useMemo } from 'react';
import despia from 'despia-native';

/**
 * Hook to detect if running in Despia native container
 * and provide wrapper functions for native features
 */
export const useDespia = () => {
  const platform = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return { isNative: false, isIOS: false, isAndroid: false };
    }
    const userAgent = navigator.userAgent.toLowerCase();
    const isNative = userAgent.includes('despia');
    
    return {
      isNative,
      isIOS: isNative && (userAgent.includes('iphone') || userAgent.includes('ipad')),
      isAndroid: isNative && userAgent.includes('android'),
    };
  }, []);

  const { isNative, isIOS, isAndroid } = platform;

  // Haptic feedback
  const hapticLight = useCallback(() => {
    if (isNative) despia('lighthaptic://');
  }, [isNative]);

  const hapticHeavy = useCallback(() => {
    if (isNative) despia('heavyhaptic://');
  }, [isNative]);

  const hapticSuccess = useCallback(() => {
    if (isNative) despia('successhaptic://');
  }, [isNative]);

  const hapticWarning = useCallback(() => {
    if (isNative) despia('warninghaptic://');
  }, [isNative]);

  const hapticError = useCallback(() => {
    if (isNative) despia('errorhaptic://');
  }, [isNative]);

  // UI controls
  const showSpinner = useCallback(() => {
    if (isNative) despia('spinneron://');
  }, [isNative]);

  const hideSpinner = useCallback(() => {
    if (isNative) despia('spinneroff://');
  }, [isNative]);

  // Sharing
  const share = useCallback((message: string, url?: string) => {
    if (isNative) {
      const encodedMessage = encodeURIComponent(message);
      const shareUrl = url ? `&url=${encodeURIComponent(url)}` : '';
      despia(`shareapp://message?=${encodedMessage}${shareUrl}`);
    }
  }, [isNative]);

  // Screenshot
  const takeScreenshot = useCallback(() => {
    if (isNative) despia('takescreenshot://');
  }, [isNative]);

  // Save image
  const saveImage = useCallback((imageUrl: string) => {
    if (isNative) despia(`savethisimage://?url=${encodeURIComponent(imageUrl)}`);
  }, [isNative]);

  // Clipboard
  const getClipboard = useCallback(async () => {
    if (isNative) {
      const data = await despia('getclipboard://', ['clipboarddata']);
      return data.clipboarddata;
    }
    return null;
  }, [isNative]);

  // App info
  const getAppVersion = useCallback(async () => {
    if (isNative) {
      const info = await despia('getappversion://', ['versionNumber', 'bundleNumber']);
      return info;
    }
    return null;
  }, [isNative]);

  // Device ID
  const getDeviceId = useCallback(async () => {
    if (isNative) {
      const data = await despia('get-uuid://', ['uuid']);
      return data.uuid;
    }
    return null;
  }, [isNative]);

  return {
    isNative,
    isIOS,
    isAndroid,
    // Haptics
    hapticLight,
    hapticHeavy,
    hapticSuccess,
    hapticWarning,
    hapticError,
    // UI
    showSpinner,
    hideSpinner,
    // Sharing
    share,
    takeScreenshot,
    saveImage,
    // Device
    getClipboard,
    getAppVersion,
    getDeviceId,
  };
};

// Standalone checks for use outside React components
export const isDespiaRuntime = () => {
  if (typeof navigator === 'undefined') return false;
  return navigator.userAgent.toLowerCase().includes('despia');
};

export const isDespiaIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('despia') && 
         (userAgent.includes('iphone') || userAgent.includes('ipad'));
};

export const isDespiaAndroid = () => {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('despia') && userAgent.includes('android');
};
